import dayjs from 'dayjs';

import type {
  Booking,
  BookingWithPackageUsage,
  CustomerPackage,
  CustomerPackageStatus,
} from '@/models/booking';
import type {
  AuthLoginRequest,
  AuthSignupRequest,
  PagedResponse,
  Profile,
  UpdateMeRequest,
  User,
} from '@/models/profile';
import { useBookingStore } from '@/store/bookingStore';
import { useUserStore } from '@/store/userStore';
import { sortCountriesByPriority } from '@/utils/countryPriority';

import { BookingService } from './bookingService';
import { profileApiClient } from './profileAPI';
import { STORAGE_KEYS } from './storageKeys';

let nationalitiesCache: { code: string; name: string }[] | null = null;

export class ProfileService {
  static isLoggedIn(): boolean {
    return !!localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  }

  static logout(): void {
    // Clear in-memory profile-related data first.
    useUserStore.getState().reset();
    useBookingStore.getState().reset();

    const keysToClear = [
      STORAGE_KEYS.AUTH_TOKEN,
      STORAGE_KEYS.OTP_REF,
      STORAGE_KEYS.BRAND_ID,
      STORAGE_KEYS.STORE_ID,
      STORAGE_KEYS.USER_STORE,
      STORAGE_KEYS.BOOKING_STORE,
      STORAGE_KEYS.BOOKING_DONE,
    ];

    keysToClear.forEach((key) => {
      localStorage.removeItem(key);
    });
  }

  /**
   * Legacy local-only persistence.
   * The new backend profile flow should use Auth + /users/me endpoints.
   */
  static saveProfile(profile: Profile): void {
    this.setStoredProfile(profile);
  }

  static async login(
    request: AuthLoginRequest,
  ): Promise<{ token: string; user: User }> {
    const response = await profileApiClient.login(request);
    this.userToProfile(response.user);
    return response;
  }

  static async signup(
    request: AuthSignupRequest,
  ): Promise<{ token: string; user: User }> {
    const response = await profileApiClient.signup(request);
    this.userToProfile(response.user);
    return response;
  }

  static async checkAuthExists(
    telPrefix: string,
    tel: string,
  ): Promise<boolean> {
    return await profileApiClient.checkAuthExists(telPrefix, tel);
  }

  static async getMe(): Promise<User> {
    return await profileApiClient.getMe();
  }

  static async updateMe(request: UpdateMeRequest): Promise<User> {
    const user = await profileApiClient.updateMe(request);
    this.userToProfile(user);
    return user;
  }

  static async getMyBookings(params?: {
    limit?: number;
    page?: number;
  }): Promise<PagedResponse<Booking>> {
    return await profileApiClient.getMyBookings(params);
  }

  static async getMyCustomerPackages(params?: {
    limit?: number;
    page?: number;
    usable?: boolean;
  }): Promise<PagedResponse<CustomerPackage>> {
    return await profileApiClient.getMyCustomerPackages(params);
  }

  static async getMyCustomerPackagesStatus(): Promise<CustomerPackageStatus> {
    return await profileApiClient.getMyCustomerPackagesStatus();
  }

  static async getCustomerPackageById(id: number): Promise<CustomerPackage> {
    return await profileApiClient.getCustomerPackagesById(id);
  }

  static async getCustomerPackageUsage(
    id: number,
    params?: { limit?: number; page?: number },
  ): Promise<PagedResponse<BookingWithPackageUsage>> {
    return await profileApiClient.getCustomerPackageUsage(id, params);
  }

  static setStoredProfile(profile: Profile): void {
    useUserStore.getState().setProfile(profile);
  }

  static setStoredPhoneNumber(phoneNumber: string): void {
    useUserStore.getState().setPhoneNumber(phoneNumber);
  }

  /**
   * Converts `User` into UI `Profile` and also persists it into the user store.
   * This keeps view components free from direct store access.
   */
  static userToProfile(user: User): Profile {
    const profile: Profile = {
      name: user.name,
      telPrefix: user.telPrefix,
      tel: user.tel,
      email: user.email,
      gender: user.gender,
      nationalityCode: user.nationality,
      birthday: user.birthday
        ? this.toBirthdayISO(new Date(user.birthday))
        : undefined,
    };

    this.setStoredProfile(profile);

    const phoneNumber = `${user.telPrefix ?? ''}${user.tel ?? ''}`;
    if (phoneNumber) this.setStoredPhoneNumber(phoneNumber);

    return profile;
  }

  static getStoredProfile(): Profile | null {
    return useUserStore.getState().profile;
  }

  static async getNationalities(): Promise<{ code: string; name: string }[]> {
    if (nationalitiesCache) return nationalitiesCache;
    const countries = await BookingService.getCountries();
    nationalitiesCache = sortCountriesByPriority(
      countries.map((c) => ({ code: c.code, name: c.name })),
    );
    return nationalitiesCache;
  }

  static toBirthdayISO(date: Date): string {
    // We only store day+month (no year). Use reference year to keep stable.
    return dayjs(date).year(2000).startOf('day').toISOString();
  }

  static toBirthdayYmd(date: Date): string {
    // Backend expects YYYY-MM-DD. We only capture day+month, so use reference year.
    return dayjs(date).year(2000).format('YYYY-MM-DD');
  }

  static birthdayIsoToYmd(iso: string): string {
    // Convert stored ISO (used for UI) into date-only string without timezone drift.
    return dayjs(iso).year(2000).format('YYYY-MM-DD');
  }
}

// Service-level hook wrappers so view layer doesn't import the store.
export const useStoredProfile = (): Profile | null =>
  useUserStore((s) => s.profile);

export const useStoredPhoneNumber = (): string =>
  useUserStore((s) => s.phoneNumber);
